<template>
  <v-container class="py-7">
    <v-row>
      <v-col cols="12">
        <div class="title-container">
          <div class="title-block">
            <span class="page-title">Usuários do Juízo</span>
            <v-breadcrumbs class="app-breadcrumb" divider="›" :items="breadcrumbs" />
          </div>
          <div class="button-group">
            <v-btn v-if="$check(user, ['judiciaryusers.create'])" color="primary" elevation="0" to="/admins-juizo/novo">
              Incluir
            </v-btn>
          </div>
        </div>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <v-card class="table-card">
          <v-card-title class="filter-card-title">Filtros</v-card-title>
          <v-form @submit.prevent="load(true)">
            <v-card-text>
              <v-row>
                <v-col cols="12" md="4">
                  <v-text-field v-model="filters.content" label="Nome ou e-mail" dense outlined hide-details clearable />
                </v-col>
                <v-col cols="12" md="3">
                  <v-select v-model="filters.judiciary_unit_id" :items="units" item-value="id" item-text="name" label="Unidade judicial" dense outlined hide-details clearable />
                </v-col>
                <v-col cols="12" md="2">
                  <v-select v-model="filters.role" :items="roles" item-value="id" item-text="name" label="Perfil" dense outlined hide-details clearable />
                </v-col>
                <v-col cols="12" md="2">
                  <v-select v-model="filters.status" :items="statuses" item-value="id" item-text="name" label="Status" dense outlined hide-details clearable />
                </v-col>
                <v-col cols="12" md="1" class="d-flex align-center justify-end">
                  <v-btn icon color="primary" type="submit" title="Filtrar"><v-icon>mdi-magnify</v-icon></v-btn>
                  <v-btn icon color="primary" type="button" title="Limpar filtros" @click="clearFilters"><v-icon>mdi-filter-remove-outline</v-icon></v-btn>
                </v-col>
              </v-row>
            </v-card-text>
          </v-form>

          <v-divider />
          <v-card-title class="table-card-title">Acessos cadastrados</v-card-title>
          <v-card-text>
            <v-data-table
              :headers="headers"
              :items="items"
              :server-items-length="total"
              :options.sync="options"
              :footer-props="footerOptions"
              :loading="loading"
              disable-sort
              class="app-table hover-table"
              no-data-text="Nenhum usuário do Juízo foi encontrado"
              loading-text="Carregando dados..."
              @update:page="pageChanged"
              @update:items-per-page="pageSizeChanged"
            >
              <template v-slot:[`item.role`]="{ item }">{{ roleName(item.role) }}</template>
              <template v-slot:[`item.status`]="{ item }">
                <v-chip small :color="statusColor(item.status)" dark>{{ statusName(item.status) }}</v-chip>
              </template>
              <template v-slot:[`item.actions`]="{ item }">
                <v-icon class="mr-2" :disabled="!$check(user, ['judiciaryusers.find'])" @click="viewItem(item)">
                  {{ $check(user, ['judiciaryusers.update']) ? 'mdi-pencil-outline' : 'mdi-eye-outline' }}
                </v-icon>
                <v-icon v-if="Number(item.status) === 0" class="mr-2" :disabled="!$check(user, ['judiciaryusers.update'])" title="Reenviar código de ativação" @click="resendInvite(item)">
                  mdi-email-sync-outline
                </v-icon>
                <v-icon :disabled="!$check(user, ['judiciaryusers.disable']) || Number(item.status) === 3" title="Inativar acesso" @click="disable(item)">
                  mdi-account-cancel-outline
                </v-icon>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import axios from "axios";

export default {
  name: "JudiciaryUserList",
  data: () => ({
    user: null,
    loading: false,
    items: [],
    total: 0,
    units: [],
    filters: { content: "", judiciary_unit_id: null, role: null, status: null },
    roles: [
      { id: "JUDGE", name: "Juiz(a)" },
      { id: "COURT_STAFF", name: "Servidor(a)" },
      { id: "COURT_ADMIN", name: "Administrador(a) da unidade" },
    ],
    statuses: [
      { id: 0, name: "Convidado" },
      { id: 1, name: "Ativo" },
      { id: 2, name: "Bloqueado" },
      { id: 3, name: "Inativo" },
    ],
    headers: [
      { text: "Nome", value: "name" },
      { text: "E-mail", value: "email" },
      { text: "Unidade judicial", value: "unit.name" },
      { text: "Caso", value: "client.name" },
      { text: "Perfil", value: "role" },
      { text: "Status", value: "status" },
      { text: "Último acesso", value: "last_login_at" },
      { text: "Ações", value: "actions", sortable: false },
    ],
    options: { page: 1, itemsPerPage: 10 },
    footerOptions: {
      itemsPerPageText: "Itens por página:",
      itemsPerPageOptions: [10, 20, 30, 50],
      pageText: "{0}-{1} de {2}",
    },
    breadcrumbs: [
      { text: "Homepage", disabled: false, to: "/home", exact: true },
      { text: "Usuários do Juízo", disabled: true },
    ],
  }),
  created() {
    this.user = this.$root.user.data || this.$root.user;
    this.loadUnits();
    this.load();
  },
  methods: {
    roleName(value) {
      const item = this.roles.find(role => role.id === value);
      return item ? item.name : value;
    },
    statusName(value) {
      const item = this.statuses.find(status => status.id === Number(value));
      return item ? item.name : value;
    },
    statusColor(value) {
      return ({ 0: "orange darken-1", 1: "green", 2: "red", 3: "grey" })[Number(value)] || "grey";
    },
    pageChanged(page) {
      this.options.page = page;
      this.load();
    },
    pageSizeChanged(size) {
      this.options.itemsPerPage = size;
      this.load(true);
    },
    clearFilters() {
      this.filters = { content: "", judiciary_unit_id: null, role: null, status: null };
      this.load(true);
    },
    async load(resetPage = false) {
      if (resetPage) this.options.page = 1;
      this.loading = true;
      try {
        const search = Object.fromEntries(Object.entries(this.filters).filter(([, value]) => value !== null && value !== ""));
        const response = await axios.get("/api/admin/judiciary-users", {
          params: { page: this.options.page, limit: this.options.itemsPerPage, search: JSON.stringify(search) },
        });
        this.items = response.data.content.data;
        this.total = response.data.content.paging.total;
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 2500 });
      } finally {
        this.loading = false;
      }
    },
    async loadUnits() {
      try {
        const response = await axios.get("/api/admin/judiciary-units");
        this.units = response.data.content;
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 2500 });
      }
    },
    viewItem(item) {
      this.$router.push(`/admins-juizo/${item.id}`);
    },
    async resendInvite(item) {
      const confirmed = await this.$dialog.confirm({
        title: "Reenviar ativação",
        text: `Enviar um novo código de 6 dígitos para ${item.email}?`,
        actions: { false: "Cancelar", true: "Enviar" },
      });
      if (!confirmed) return;
      try {
        await axios.post(`/api/admin/judiciary-users/${item.id}/resend-invite`);
        this.$dialog.notify.info("Código de ativação enviado.", { position: "top-right", timeout: 2500 });
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 2500 });
      }
    },
    async disable(item) {
      const confirmed = await this.$dialog.confirm({
        title: "Inativar acesso",
        text: `Inativar o acesso de ${item.name}? As sessões atuais serão revogadas.`,
        actions: { false: "Cancelar", true: "Inativar" },
      });
      if (!confirmed) return;
      try {
        await axios.post(`/api/admin/judiciary-users/${item.id}/disable`);
        this.$dialog.notify.info("Acesso inativado.", { position: "top-right", timeout: 2500 });
        this.load();
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 2500 });
      }
    },
  },
};
</script>
